import React, { useState } from 'react';
import './SocietyManagerModal.css';
import { FiPlus, FiHash, FiCheck, FiX, FiShare2, FiCopy, FiSearch, FiClock, FiAlertTriangle, FiEdit2, FiLogOut, FiShield } from 'react-icons/fi';
import { useAuth } from '../AuthContext';
import API_URL from '../config';
import { joinSocietyUrl } from '../utils/shareLinks';

const SocietyManagerModal = ({ isOpen, onClose, onShutdownRequest }) => {
    const {
        userProfile, currentSociety, setCurrentSociety, currentUser, fetchUserProfile,
        isViewingAsAdmin, adminSociety, exitAdminSociety,
    } = useAuth();
    const [isCreating, setIsCreating] = useState(false);
    const [isJoining, setIsJoining] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [societyName, setSocietyName] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [requestedIds, setRequestedIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [editingSocietyId, setEditingSocietyId] = useState(null);
    const [editName, setEditName] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [editError, setEditError] = useState('');
    const [leavingId, setLeavingId] = useState(null);     // asking to confirm
    const [leaveBusyId, setLeaveBusyId] = useState(null); // request in flight
    const [leaveError, setLeaveError] = useState('');

    if (!isOpen) return null;

    const handleShare = async (e, society) => {
        e.stopPropagation();
        const shareUrl = joinSocietyUrl(society.invite_code);
        const shareData = {
            title: `Join ${society.name} on 1-19 Golf`,
            text: `Join my golf society "${society.name}" on 1-19 Golf!\n\n${shareUrl}\n\nInvite code: ${society.invite_code}`,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                console.error('Error sharing:', err);
            }
        } else {
            copyToClipboard(e, shareData.text);
        }
    };

    const copyToClipboard = (e, text) => {
        e.stopPropagation();
        const textToCopy = text;
        navigator.clipboard.writeText(textToCopy);
        setCopied(textToCopy); // Store the copied text or ID to show feedback specifically for this item?
        // Using simple state might flash all buttons. Ideally tracking which one was copied.
        // For simplicity, we'll just set a global 'copied' state and maybe it shows on all? 
        // Or we can just use the generic 'copied' boolean and accept it might flash elsewhere or just show check briefly.
        // Let's rely on the timeout.
        
        // Actually, to show checkmark only on the clicked button, we need better state.
        // But for now let's keep it simple or use a local variable if we could.
        // Given constraints, I'll just set 'copied' to true. 
        // If the user wants precise feedback per card, we need a map of states.
        // Let's try to keep it simple first.
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        const q = searchQuery.trim();
        if (q.length < 3) return;
        setSearching(true);
        setHasSearched(true);
        setSearchResults([]);
        setError('');
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_URL}/societies/search?q=${encodeURIComponent(q)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSearchResults(data);
                const alreadyRequested = new Set();
                data.forEach(s => { if (s.has_pending_request) alreadyRequested.add(s.id); });
                setRequestedIds(prev => new Set([...prev, ...alreadyRequested]));
            }
        } catch {
            // Silently fail search
        }
        setSearching(false);
    };

    const handleRequestJoin = async (societyId) => {
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_URL}/societies/${societyId}/request-join`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await res.json();
            if (res.ok) {
                setRequestedIds(prev => new Set([...prev, societyId]));
            } else if (data.error === 'You are already a member of this society') {
                await fetchUserProfile(currentUser);
            } else {
                setError(data.error || 'Failed to send request.');
            }
        } catch {
            setError('Something went wrong. Please try again.');
        }
    };

    const handleCreateSociety = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/auth/setup-society`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: societyName })
            });
            const data = await response.json();
            if (response.ok) {
                // Fetch updated profile
                const updatedData = await fetchUserProfile(currentUser);
                
                // Auto-select the newly created society
                // The API returns the new society object in data.society
                if (data.society) {
                    setCurrentSociety(data.society);
                } else if (updatedData?.societies?.length > 0) {
                    // Fallback: select the last one in the list (usually the newest)
                    setCurrentSociety(updatedData.societies[updatedData.societies.length - 1]);
                }

                setIsCreating(false);
                setSocietyName('');
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to create society');
        }
        setLoading(false);
    };

    const handleJoinSociety = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/auth/join-society`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ invite_code: inviteCode })
            });
            const data = await response.json();
            if (response.ok) {
                const updatedData = await fetchUserProfile(currentUser);
                
                // Try to find the joined society in the updated list
                if (updatedData?.societies) {
                    const joinedSociety = updatedData.societies.find(s => s.invite_code === inviteCode.toUpperCase());
                    if (joinedSociety) {
                        setCurrentSociety(joinedSociety);
                    }
                }

                setIsJoining(false);
                setInviteCode('');
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Failed to join society');
        }
        setLoading(false);
    };

    const startEditing = (e, society) => {
        e.stopPropagation();
        setEditingSocietyId(society.id);
        setEditName(society.name);
        setEditError('');
    };

    const cancelEditing = (e) => {
        if (e) e.stopPropagation();
        setEditingSocietyId(null);
        setEditName('');
        setEditError('');
    };

    const handleRenameSociety = async (e, society) => {
        e.preventDefault();
        e.stopPropagation();
        const name = editName.trim();
        if (name === society.name) {
            cancelEditing();
            return;
        }
        if (name.length < 2) {
            setEditError('Society name must be at least 2 characters.');
            return;
        }
        setSavingName(true);
        setEditError('');
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/societies/${society.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name })
            });
            const data = await response.json();
            if (response.ok) {
                // Refreshes the society list and re-syncs currentSociety
                await fetchUserProfile(currentUser);
                cancelEditing();
            } else {
                setEditError(data.error || 'Failed to rename society.');
            }
        } catch (err) {
            setEditError('Failed to rename society.');
        }
        setSavingName(false);
    };

    /* Leave a society.
       The player profile and every round stay behind — a society's results are
       the society's, and deleting them would rewrite past leaderboards for
       everyone still in it. The server refuses for the owner (they would orphan
       it) and for the last admin, and those refusals are shown as given. */
    const handleLeaveSociety = async (society) => {
        if (leaveBusyId) return;
        setLeaveBusyId(society.id);
        setLeaveError('');
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_URL}/societies/${society.id}/leave`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Society-ID': society.id,
                    'Content-Type': 'application/json',
                },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setLeaveError(data.error || 'Could not leave the society. Please try again.');
                return;
            }
            setLeavingId(null);
            // Refresh first, so the society we switch to is one we are still in.
            const updated = await fetchUserProfile(currentUser);
            if (String(currentSociety?.id) === String(society.id)) {
                const remaining = updated?.societies?.find(s => String(s.id) !== String(society.id));
                setCurrentSociety(remaining || null);
            }
        } catch (e) {
            setLeaveError('Could not reach the server, so nothing changed. Check your connection.');
        } finally {
            setLeaveBusyId(null);
        }
    };

    const ownedSocieties = userProfile?.societies?.filter(s => s.role === 'ADMIN') || [];
    const joinedSocieties = userProfile?.societies?.filter(s => s.role !== 'ADMIN') || [];

    const isOwner = (society) =>
        userProfile?.is_system_admin || userProfile?.id === society.owner_id;

    // Deliberately NOT isOwner: that one is true for any system admin, and a
    // system admin who has genuinely joined a society may leave it like anyone
    // else. Only the actual owner is barred, because leaving would orphan it.
    const ownsIt = (society) => userProfile?.id === society.owner_id;

    const canShutDown = (society) => onShutdownRequest && isOwner(society);

    const renderSocietyList = (societies, emptyMessage) => {
        if (societies.length === 0) {
            return <p className="no-societies-text">{emptyMessage}</p>;
        }
        return societies.map(society => (
            <div
                key={society.id}
                className={`society-list-item ${currentSociety?.id === society.id ? 'active' : ''}`}
                onClick={() => editingSocietyId !== society.id && setCurrentSociety(society)}
            >
                <div className="society-card-header">
                    <div className="society-item-info">
                        <span className="item-name">{society.name}</span>
                        {currentSociety?.id === society.id && <span className="item-status">Active</span>}
                    </div>
                    {currentSociety?.id === society.id && <FiCheck className="check-icon" />}
                </div>

                {editingSocietyId === society.id ? (
                    <div className="smm-edit-panel" onClick={(e) => e.stopPropagation()}>
                        <form className="smm-rename-form" onSubmit={(e) => handleRenameSociety(e, society)}>
                            <label className="smm-edit-label" htmlFor={`smm-name-${society.id}`}>
                                Society name
                            </label>
                            <input
                                id={`smm-name-${society.id}`}
                                type="text"
                                className="smm-rename-input"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                maxLength={120}
                                autoFocus
                            />
                            {editError && <span className="smm-rename-error">{editError}</span>}
                            <div className="smm-rename-actions">
                                <button type="submit" className="smm-rename-save" disabled={savingName}>
                                    {savingName ? 'Saving…' : 'Save changes'}
                                </button>
                                <button type="button" className="smm-rename-cancel" onClick={cancelEditing} disabled={savingName}>
                                    Cancel
                                </button>
                            </div>
                        </form>

                        {canShutDown(society) && (
                            <div className="smm-danger-zone">
                                <button
                                    type="button"
                                    className="smm-shutdown-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentSociety(society);
                                        onClose();
                                        onShutdownRequest();
                                    }}
                                >
                                    <FiAlertTriangle size={14} /> Shut Down Society
                                </button>
                                <span className="smm-danger-hint">
                                    Archives the society for every member. Scoring history is kept.
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="society-card-footer">
                            <span className="code-label">Code: <strong>{society.invite_code}</strong></span>
                            <div className="card-actions">
                                <button className="smm-action-btn" onClick={(e) => copyToClipboard(e, society.invite_code)}>
                                    {copied ? <FiCheck color="#28a745" /> : <FiCopy />} Copy code
                                </button>
                                <button className="smm-action-btn" onClick={(e) => handleShare(e, society)}>
                                    <FiShare2 /> Share
                                </button>
                            </div>
                        </div>
                        {isOwner(society) && (
                            <button
                                className="smm-action-btn smm-action-btn--edit"
                                onClick={(e) => startEditing(e, society)}
                            >
                                <FiEdit2 /> Edit society
                            </button>
                        )}

                        {/* The owner has no Leave — they would orphan the
                            society. Shut Down, in Edit society, is their door. */}
                        {!ownsIt(society) && (
                            leavingId === society.id ? (
                                <div className="smm-leave-confirm" onClick={(e) => e.stopPropagation()}>
                                    <p className="smm-leave-question">Leave {society.name}?</p>
                                    <p className="smm-leave-note">
                                        Your scores and results stay with the society. You can
                                        rejoin later with the invite code.
                                    </p>
                                    {leaveError && <p className="smm-leave-error">{leaveError}</p>}
                                    <div className="smm-leave-actions">
                                        <button
                                            className="smm-leave-cancel"
                                            onClick={() => { setLeavingId(null); setLeaveError(''); }}
                                            disabled={leaveBusyId === society.id}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="smm-leave-confirm-btn"
                                            onClick={() => handleLeaveSociety(society)}
                                            disabled={leaveBusyId === society.id}
                                        >
                                            {leaveBusyId === society.id ? 'Leaving…' : 'Leave society'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    className="smm-action-btn smm-action-btn--leave"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLeaveError('');
                                        setLeavingId(society.id);
                                    }}
                                >
                                    <FiLogOut /> Leave society
                                </button>
                            )
                        )}
                    </>
                )}
            </div>
        ));
    };

    return (
        <div className="modal-overlay">
            <div className="society-modal-content">
                <div className="modal-header">
                    <h3>Manage Societies</h3>
                    <button className="close-btn" onClick={onClose}><FiX size={24} /></button>
                </div>

                {/* A society opened from the admin dashboard is not in the lists
                    below — it is not one of yours. Say so, and offer the way out,
                    because otherwise the only clue is a name you don't recognise
                    in the header. */}
                {isViewingAsAdmin && adminSociety && (
                    <div className="smm-admin-view">
                        <div className="smm-admin-view-text">
                            <span className="smm-admin-view-label">
                                <FiShield size={13} /> Admin view
                            </span>
                            <span className="smm-admin-view-name">{adminSociety.name}</span>
                            <span className="smm-admin-view-note">
                                You are not a member of this society.
                            </span>
                        </div>
                        <button
                            className="smm-admin-view-exit"
                            onClick={() => { exitAdminSociety(); onClose(); }}
                        >
                            Close
                        </button>
                    </div>
                )}

                <div className="society-actions">
                    <button className={`action-btn ${isSearching ? 'active' : ''}`} onClick={() => { setIsSearching(!isSearching); setIsCreating(false); setIsJoining(false); setError(''); }}>
                        <FiSearch /> Find
                    </button>
                    <button className={`action-btn ${isJoining ? 'active' : ''}`} onClick={() => { setIsJoining(!isJoining); setIsCreating(false); setIsSearching(false); setError(''); }}>
                        <FiHash /> Code
                    </button>
                    <button className={`action-btn ${isCreating ? 'active' : ''}`} onClick={() => { setIsCreating(!isCreating); setIsJoining(false); setIsSearching(false); setError(''); }}>
                        <FiPlus /> New
                    </button>
                </div>

                {error && <div className="modal-error">{error}</div>}

                {isCreating && (
                    <form className="modal-form" onSubmit={handleCreateSociety}>
                        <input
                            type="text"
                            placeholder="Society Name"
                            value={societyName}
                            onChange={(e) => setSocietyName(e.target.value)}
                            required
                        />
                        <button type="submit" disabled={loading}>{loading ? '...' : 'Create'}</button>
                    </form>
                )}

                {isJoining && (
                    <form className="modal-form" onSubmit={handleJoinSociety}>
                        <input
                            type="text"
                            placeholder="Enter Invite Code"
                            value={inviteCode}
                            onChange={(e) => setInviteCode(e.target.value)}
                            required
                        />
                        <button type="submit" disabled={loading}>{loading ? '...' : 'Join'}</button>
                    </form>
                )}

                {isSearching && (
                    <div className="smm-search-section">
                        <form className="smm-search-form" onSubmit={handleSearch}>
                            <input
                                type="search"
                                className="smm-search-input"
                                placeholder="Society or organiser name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                            />
                            <button
                                type="submit"
                                className={`smm-search-btn${searchQuery.trim().length >= 3 ? ' ready' : ''}`}
                                disabled={searching || searchQuery.trim().length < 3}
                            >
                                <FiSearch size={16} />
                            </button>
                        </form>

                        {searchResults.length > 0 && (
                            <div className="smm-search-results">
                                {searchResults.map(society => (
                                    <div key={society.id} className="smm-search-item">
                                        <div className="smm-search-info">
                                            <span className="smm-search-name">{society.name}</span>
                                            <span className="smm-search-admin">by {society.admin_name}</span>
                                        </div>
                                        {society.is_member ? (
                                            <span className="smm-status joined"><FiCheck size={13} /> Joined</span>
                                        ) : requestedIds.has(society.id) ? (
                                            <span className="smm-status pending"><FiClock size={13} /> Requested</span>
                                        ) : (
                                            <button className="smm-request-btn" onClick={() => handleRequestJoin(society.id)}>
                                                Request
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {!searching && hasSearched && searchResults.length === 0 && (
                            <p className="smm-search-hint">No societies found. Try a different name.</p>
                        )}
                    </div>
                )}

                <div className="society-list-scroll">
                    <div className="list-section-header">Owned by Me</div>
                    {renderSocietyList(ownedSocieties, "You haven't created any societies yet.")}

                    <div className="list-section-header" style={{ marginTop: '15px' }}>Joined Societies</div>
                    {renderSocietyList(joinedSocieties, "You haven't joined any other societies.")}
                </div>

            </div>
        </div>
    );
};

export default SocietyManagerModal;
